// lab 페이지(버핏·유사기업)의 탐색-생존 상태 — useState 대체.
//
// 문제: 분석이 도는 중에 다른 페이지로 나가면 컴포넌트가 언마운트되며 진행
// 상태가 사라지고, 돌아와도 빈 화면이 된다 (사용자 요청: 나가도 계속 돌고,
// 돌아오면 작동 중인 화면 그대로).
//
// 해법: 상태를 모듈 레벨 Map 에 두고 useSyncExternalStore 로 구독한다.
// setter 는 컴포넌트 밖에서도 유효하므로 pollTask/리플레이 콜백이 언마운트
// 후에도 계속 기록하고, 재마운트 시 Map 값으로 즉시 복원된다.
// (SPA 세션 한정 — 새로고침은 서버 task 폴링이 아니라 재실행이 필요하다)
import { useCallback, useSyncExternalStore } from "react";

const store = new Map<string, unknown>();
const subs = new Map<string, Set<() => void>>();

function notify(key: string) {
  const s = subs.get(key);
  if (s) for (const cb of s) cb();
}

/** 컴포넌트 밖(폴링 콜백 등)에서도 안전한 setter. */
export function labSet<T>(key: string, v: T | ((prev: T) => T)): void {
  const prev = store.get(key) as T;
  const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
  if (Object.is(prev, next)) return;
  store.set(key, next);
  notify(key);
}

export function labGet<T>(key: string): T | undefined {
  return store.get(key) as T | undefined;
}

export function useLabState<T>(key: string, init: T): [T, (v: T | ((p: T) => T)) => void] {
  // 첫 사용 시 시드 — getSnapshot 이 항상 Map 의 안정된 참조를 돌려주게 한다
  if (!store.has(key)) store.set(key, init);
  const value = useSyncExternalStore(
    useCallback((cb: () => void) => {
      let s = subs.get(key);
      if (!s) {
        s = new Set();
        subs.set(key, s);
      }
      s.add(cb);
      return () => { s!.delete(cb); };
    }, [key]),
    () => store.get(key) as T,
  );
  const set = useCallback((v: T | ((p: T) => T)) => labSet(key, v), [key]);
  return [value, set];
}

// ── 하트비트 — 긴 분석 동안 '살아 있음'을 주기적으로 로그에 남긴다 ──
const beats = new Map<string, number>();

export function labHeartbeat(key: string, startedAt: number, onBeat: (line: string) => void, everyMs = 20000): void {
  labStopHeartbeat(key);
  const t = window.setInterval(() => {
    const s = Math.floor((Date.now() - startedAt) / 1000);
    const mm = Math.floor(s / 60);
    // 이모지를 쓰지 않는다. 진행 카드가 이 줄을 '마지막 갱신' 판정에만 쓰고
    // 표에는 싣지 않으므로 접두 문구는 고정해 둔다(HEARTBEAT_RE 와 짝).
    onBeat(`분석 계속 진행 중입니다 — 경과 ${mm ? `${mm}분 ` : ""}${s % 60}초`);
  }, everyMs);
  beats.set(key, t);
}

export function labStopHeartbeat(key: string): void {
  const t = beats.get(key);
  if (t) {
    window.clearInterval(t);
    beats.delete(key);
  }
}
